import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useAssets as useAssetsHook } from "../../assets/api/assets";
import { useTheme } from "../../common/contexts/ThemeContext";

function AssetSelector({
  selectedAssets,
  onChange,
  label = "Assets Access *",
}) {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { colors } = useTheme();

  // Only fetch assets when there's a search query
  const shouldFetch = debouncedSearch.trim().length > 0;
  const { assets, loading: assetsLoading } = useAssetsHook(
    1,
    100,
    shouldFetch ? debouncedSearch : "",
  );

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  return (
    <FormControl fullWidth sx={{ mb: 3 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={selectedAssets}
        onChange={(e) => onChange(e.target.value)}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selected.map((assetId) => {
              const asset = assets.find((p) => p.id === assetId);
              return (
                <Chip
                  key={assetId}
                  label={asset?.title || `Asset ${assetId}`}
                  size="small"
                />
              );
            })}
          </Box>
        )}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 400,
            },
          },
        }}
      >
        {/* Search Field Inside Dropdown */}
        <Box
          sx={{
            px: 2,
            py: 2,
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Search assets..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            InputProps={{
              startAdornment: (
                <Search
                  size={18}
                  color={colors.textSecondary}
                  style={{ marginRight: 8 }}
                />
              ),
              endAdornment:
                assetsLoading && shouldFetch ? (
                  <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
                    <CircularProgress color="primary" size={18} />
                  </Box>
                ) : null,
            }}
            onClick={(e) => e.stopPropagation()}
            autoFocus
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "transparent",
              },
            }}
          />
        </Box>

        {/* Placeholder when no search */}
        {!shouldFetch && (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <Search size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
              Search for assets
            </Typography>
            <Typography variant="body2">
              Type in the search box above to find and select assets
            </Typography>
          </Box>
        )}

        {/* Loading State */}
        {shouldFetch && assetsLoading && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              Loading assets...
            </Typography>
          </Box>
        )}

        {/* Assets List */}
        {shouldFetch &&
          !assetsLoading &&
          assets.length > 0 &&
          assets.map((asset) => (
            <MenuItem key={asset.id} value={asset.id}>
              <ListItemIcon>
                <Checkbox
                  checked={selectedAssets.includes(asset.id)}
                  size="small"
                />
              </ListItemIcon>
              <ListItemText primary={asset.title} />
            </MenuItem>
          ))}

        {/* No Results */}
        {shouldFetch && !assetsLoading && assets.length === 0 && (
          <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
            <Typography variant="body2">
              No assets found for "{searchValue}"
            </Typography>
          </Box>
        )}
      </Select>
    </FormControl>
  );
}

export default AssetSelector;
